/**
 * Wave 4.07 — CAD Developer master form dialog. Basic create/edit for the
 * CAD Developer party type (the person/studio that develops the 3D CAD for
 * an order). Mirrors the Cutter form dialog but with only the light-touch
 * fields a CAD developer needs.
 *
 * Also usable for INLINE creation from the production-checklist CAD combobox:
 * pass ``defaultName`` to pre-fill the name and ``onCreated`` to receive the
 * freshly-created developer object so the caller can auto-select it.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { extractError } from '@/lib/errors';
import { PencilRuler } from 'lucide-react';

export function CadDeveloperFormDialog({
  open, onOpenChange, developer, companyId, onSaved,
  defaultName = '', onCreated,
}) {
  const editing = Boolean(developer?.id);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: developer?.name || defaultName || '',
      phone: developer?.phone || '',
      email: developer?.email || '',
      notes: developer?.notes || '',
    });
    // Only re-seed the form when the dialog OPENS (or the edited developer
    // changes). ``defaultName`` is intentionally excluded: the combobox resets
    // its search query to '' when its popover closes, which would otherwise
    // re-run this effect and wipe the pre-filled name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, developer?.id]);

  const submit = async () => {
    if (!form.name?.trim()) {
      toast.error('CAD Developer name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, company_id: companyId };
      if (editing) {
        const { data } = await api.put(`/masters/cad-developers/${developer.id}`, payload);
        toast.success('CAD Developer updated');
        onOpenChange(false);
        if (typeof onSaved === 'function') onSaved(data);
      } else {
        const { data } = await api.post('/masters/cad-developers', payload);
        toast.success('CAD Developer created');
        onOpenChange(false);
        if (typeof onSaved === 'function') onSaved(data);
        if (typeof onCreated === 'function') onCreated(data);
      }
    } catch (e) {
      toast.error(extractError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cad-developer-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilRuler size={18} className="text-[var(--ink-navy)]" />
            {editing ? `Edit CAD Developer · ${developer?.name}` : 'New CAD Developer'}
          </DialogTitle>
          <DialogDescription>
            CAD Developers design the 3D CAD for an order. They can be attributed on the
            CAD production step and reported in the CAD Development report.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name <span className="text-rose-600">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="cad-developer-form-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="cad-developer-form-phone"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cad-developer-form-email"
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="cad-developer-form-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cad-developer-form-save">
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create CAD Developer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CadDeveloperFormDialog;
